import { useEffect, useRef, useState } from 'react';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { Icono } from './Iconos.js';

/**
 * LO QUE SE ABRE, SE ABRE DENTRO.
 *
 * Una foto de una incidencia o el informe del mes se abrian con un enlace en
 * otra pestana. En el navegador se apana; en la aplicacion instalada en el
 * movil NO HAY PESTANAS: el fichero ocupa la pantalla entera, sin barra de
 * direcciones y sin flecha de volver, y deja a quien lo abrio encerrado —hay
 * que cerrar la aplicacion y entrar otra vez—.
 *
 * Asi que se abre aqui: una capa encima con su boton de cerrar, la tecla Esc, y
 * el gesto de atras del movil. Las imagenes se dibujan tal cual; los PDF se
 * dibujan pagina a pagina con pdf.js, porque la respuesta de la API prohibe
 * meterse en un marco —y hace bien—.
 *
 * Queda el enlace de siempre para abrirlo fuera, que en un ordenador con dos
 * pantallas sigue siendo lo comodo.
 */

export interface Archivo {
  url: string;
  titulo: string;
  /** Si no se sabe, se averigua al descargarlo. */
  tipo?: string | null;
}

/** pdf.js pesa; se trae solo cuando hay de verdad un PDF que dibujar. */
async function dibujarPdf(datos: ArrayBuffer, destino: HTMLDivElement): Promise<void> {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
  const worker = (await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')).default;
  pdfjs.GlobalWorkerOptions.workerSrc = worker;
  const documento = await pdfjs.getDocument({ data: datos }).promise;
  destino.replaceChildren();
  const ancho = Math.min(destino.clientWidth || 900, 1400);
  for (let n = 1; n <= documento.numPages; n += 1) {
    const pagina = await documento.getPage(n);
    const base = pagina.getViewport({ scale: 1 });
    // Se dibuja al ancho disponible y con la densidad de la pantalla: un PDF
    // borroso en un movil retina no se puede leer.
    const escala = (ancho / base.width) * Math.min(window.devicePixelRatio || 1, 2);
    const vista = pagina.getViewport({ scale: escala });
    const lienzo = document.createElement('canvas');
    lienzo.width = Math.floor(vista.width);
    lienzo.height = Math.floor(vista.height);
    lienzo.className = 'visor-pagina';
    lienzo.style.width = '100%';
    const contexto = lienzo.getContext('2d');
    if (!contexto) continue;
    destino.append(lienzo);
    await pagina.render({ canvas: lienzo, canvasContext: contexto, viewport: vista }).promise;
  }
}

export function Visor({
  idioma,
  archivo,
  onCerrar,
}: {
  idioma: Idioma;
  archivo: Archivo;
  onCerrar: () => void;
}) {
  const [estado, setEstado] = useState<'cargando' | 'imagen' | 'pdf' | 'otro' | 'error'>('cargando');
  const [imagen, setImagen] = useState<string | null>(null);
  const paginas = useRef<HTMLDivElement>(null);

  /*
    EL BOTON DE ATRAS DEL MOVIL CIERRA EL VISOR, no la pantalla que hay debajo.
    Se apila una entrada de historia al abrir y se quita al cerrar: es el gesto
    que todo el mundo hace para salir de una foto.
  */
  /*
    EL BOTON DE ATRAS DEL MOVIL CIERRA EL VISOR, no la pantalla de debajo. La
    entrada de historia la apila quien abre el archivo —un clic de verdad—, y
    aqui solo se escucha la vuelta: montar y desmontar no puede tocar el
    historial, porque en desarrollo React monta cada efecto dos veces y el visor
    se cerraba solo nada mas abrirse.
  */
  const cerrar = useRef(onCerrar);
  cerrar.current = onCerrar;

  useEffect(() => {
    const atras = () => cerrar.current();
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        history.back();
      }
    };
    window.addEventListener('popstate', atras);
    window.addEventListener('keydown', tecla);
    return () => {
      window.removeEventListener('popstate', atras);
      window.removeEventListener('keydown', tecla);
    };
  }, []);

  useEffect(() => {
    let vivo = true;
    let objeto: string | null = null;
    setEstado('cargando');
    fetch(archivo.url, { credentials: 'same-origin' })
      .then(async (r) => {
        if (!r.ok) throw new Error(String(r.status));
        const datos = await r.blob();
        if (!vivo) return;
        const tipo = archivo.tipo || datos.type || '';
        if (tipo.startsWith('image/')) {
          objeto = URL.createObjectURL(datos);
          setImagen(objeto);
          setEstado('imagen');
          return;
        }
        if (tipo.includes('pdf')) {
          setEstado('pdf');
          const bytes = await datos.arrayBuffer();
          if (!vivo || !paginas.current) return;
          await dibujarPdf(bytes, paginas.current);
          return;
        }
        setEstado('otro');
      })
      .catch(() => {
        if (vivo) setEstado('error');
      });
    return () => {
      vivo = false;
      if (objeto) URL.revokeObjectURL(objeto);
    };
  }, [archivo.url, archivo.tipo]);

  return (
    <div
      className="visor"
      role="dialog"
      aria-modal="true"
      aria-label={archivo.titulo}
      onClick={(e) => {
        // Tocar el fondo cierra; tocar el documento, no.
        if (e.target === e.currentTarget) history.back();
      }}
    >
      <div className="visor-barra">
        <button type="button" className="visor-cerrar" onClick={() => history.back()}>
          <Icono nombre="volver" /> {t(idioma, 'visor.cerrar')}
        </button>
        <span className="visor-titulo">{archivo.titulo}</span>
        <a className="visor-fuera" href={archivo.url} target="_blank" rel="noreferrer">
          {t(idioma, 'visor.abrirFuera')}
        </a>
      </div>

      <div className="visor-cuerpo">
        {estado === 'cargando' && <p className="visor-aviso">{t(idioma, 'visor.cargando')}</p>}
        {estado === 'error' && <p className="visor-aviso">{t(idioma, 'visor.noSePudo')}</p>}
        {estado === 'otro' && <p className="visor-aviso">{t(idioma, 'visor.noSeVePreview')}</p>}
        {estado === 'imagen' && imagen && <img className="visor-imagen" src={imagen} alt={archivo.titulo} />}
        <div ref={paginas} className="visor-paginas" hidden={estado !== 'pdf'} />
      </div>
    </div>
  );
}
